/**
 * Numbering and URL rules of a series, mirrored from the API (EventSeriesNumberingService).
 * Keeping them here lets the form preview the edition number and the derived URL live —
 * a date change updates both without a round-trip — while the API stays the source of truth.
 */

/** The columns the API orders editions by; anything carrying them can be numbered. */
export interface INumberableEdition {
  _key: string
  id?: number
  date_start: string
  time_start: string
}

/**
 * Edition number (from 1) by `_key`, in start-date order. Editions with no date yet sort last.
 * Same tie-break as the API (`date_start, time_start, id`); a new edition has no id yet, so it
 * falls back to the array position and sorts last among same-day editions.
 */
export const editionNumbers = (editions: INumberableEdition[]): Map<string, number> => {
  const numbers = new Map<string, number>()

  editions
    .map((edition, index) => ({
      edition,
      date: edition.date_start || '9999-12-31',
      time: edition.time_start || '23:59',
      id: edition.id ?? Number.MAX_SAFE_INTEGER,
      index
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time) || a.id - b.id || a.index - b.index)
    .forEach(({ edition }, position) => numbers.set(edition._key, position + 1))

  return numbers
}

/**
 * URL an edition gets when it does not override the `slug` group: the 1st edition keeps the
 * series URL and the rest get the `-{n}edicao` suffix. Mirrors EventSeriesNumberingService::slugFor.
 */
export const automaticSlug = (seriesSlug: string, editionNumber: number): string => {
  if (!seriesSlug) return ''

  return editionNumber === 1 ? seriesSlug : `${seriesSlug}-${editionNumber}edicao`
}
